"use client";

import React, { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";

interface StatItem {
  end: number;
  decimals?: number;
  suffix: string;
  label: string;
}

const stats: StatItem[] = [
  { end: 200, suffix: "+", label: "Sites Deployed" },
  { end: 99.97, decimals: 2, suffix: "%", label: "Uptime SLA" },
  { end: 0, suffix: "", label: "Client Breaches" },
  { end: 500, suffix: "+", label: "Businesses Served" },
];

const quote = {
  text: "\u201CPro IT keeps all 17 of our branches running without us ever having to think about IT.\u201D",
  source: "helloworld Travel",
};

function easeOut(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function AnimatedCounter({ end, decimals = 0, suffix, inView }: { end: number; decimals?: number; suffix: string; inView: boolean }) {
  const [display, setDisplay] = useState("0");
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!inView || hasAnimated.current) return;
    if (end === 0) {
      setDisplay("0");
      hasAnimated.current = true;
      return;
    }

    hasAnimated.current = true;
    const duration = 2000;
    const startTime = performance.now();

    function tick(now: number) {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = easeOut(progress);
      const current = eased * end;

      if (decimals > 0) {
        setDisplay(current.toFixed(decimals));
      } else {
        setDisplay(Math.round(current).toString());
      }

      if (progress < 1) {
        requestAnimationFrame(tick);
      }
    }

    requestAnimationFrame(tick);
  }, [inView, end, decimals]);

  return (
    <>
      {display}{suffix}
    </>
  );
}

export default function TrustBar() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <div
      ref={ref}
      className="relative"
      style={{
        background: "linear-gradient(180deg, #0c1528 0%, #0a1020 100%)",
      }}
    >
      {/* Top border gradient — thicker */}
      <div
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{
          background:
            "linear-gradient(90deg, transparent 0%, rgba(37,99,235,0.6) 20%, rgba(59,130,246,0.9) 50%, rgba(34,211,238,0.5) 80%, transparent 100%)",
        }}
      />
      {/* Subtle glow below top border */}
      <div
        className="absolute top-0 left-0 right-0 h-12 pointer-events-none"
        style={{
          background: "linear-gradient(to bottom, rgba(59,130,246,0.08) 0%, transparent 100%)",
        }}
      />

      <div className="max-w-container mx-auto px-4 sm:px-6 py-6 sm:py-8 w-full">
        <div className="flex flex-wrap items-center justify-center gap-y-4">
          {/* Stats */}
          {stats.map((stat, i) => (
            <React.Fragment key={stat.label}>
              <div className="flex flex-col items-center gap-0.5 px-5 sm:px-8">
                <span
                  className="font-heading font-800 text-[24px] sm:text-[28px] leading-none"
                  style={{ color: "#F8FAFC" }}
                >
                  <AnimatedCounter
                    end={stat.end}
                    decimals={stat.decimals}
                    suffix={stat.suffix}
                    inView={inView}
                  />
                </span>
                <span
                  className="font-body text-[12px] sm:text-[13px] font-500 uppercase tracking-wider"
                  style={{ color: "#60A5FA" }}
                >
                  {stat.label}
                </span>
              </div>
              {i < stats.length - 1 && (
                <div
                  className="hidden sm:block w-px h-10"
                  style={{ background: "rgba(59,130,246,0.2)" }}
                />
              )}
            </React.Fragment>
          ))}

          {/* Divider before quote */}
          <div
            className="hidden lg:block w-px h-10 mx-4"
            style={{ background: "rgba(59,130,246,0.2)" }}
          />

          {/* Quote */}
          <div className="flex flex-col items-center lg:items-start gap-1 px-4 max-w-[400px]">
            <span
              className="font-body text-[13px] sm:text-[14px] italic leading-snug text-center lg:text-left"
              style={{ color: "rgba(203,213,225,0.7)" }}
            >
              {quote.text}
            </span>
            <span
              className="font-heading font-600 text-[11px] uppercase tracking-wider"
              style={{ color: "rgba(96,165,250,0.8)" }}
            >
              — {quote.source}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
